import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  const scrollToSection = (id: string) => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth' });
          window.scrollTo({ top: el.offsetTop, behavior: 'smooth' });
        }
      }, 300);
    } else {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const handlePageLink = (path: string) => {
    navigate(path);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const formBody = new URLSearchParams();
      formBody.append('email', email);

      const response = await fetch('https://readdy.ai/api/form/d6nm1gh4a3uojp5fh960', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitMessage('Subscribed successfully!');
        setEmail('');
      } else {
        setSubmitMessage('Something went wrong. Please try again.');
      }
    } catch (error) {
      setSubmitMessage('Unable to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer id="contact" className="bg-sage-600 text-cream-50 py-16 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center">
                <i className="ri-leaf-line text-3xl text-cream-50"></i>
              </div>
              <span className="font-serif text-2xl text-cream-50">Bodhira Co</span>
            </div>
            <p className="font-sans text-sm text-cream-100 leading-relaxed">
              Botanical wellness, handcrafted with love. Building community and spreading peace through natural self-care.
            </p>
            <div className="flex items-center gap-2 mt-3">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-phone-line text-cream-100 text-sm"></i>
              </div>
              <a href="tel:9086707882" className="font-sans text-sm text-cream-100 hover:text-cream-50 transition-colors">
                908.670.7882
              </a>
            </div>
          </div>

          <div>
            <h3 className="font-serif text-xl text-cream-50 mb-4">Quick Links</h3>
            <div className="space-y-2">
              <button
                onClick={() => scrollToSection('products')}
                className="block font-sans text-sm text-cream-100 hover:text-cream-50 transition-colors cursor-pointer"
              >
                Products
              </button>
              <button
                onClick={() => scrollToSection('classes')}
                className="block font-sans text-sm text-cream-100 hover:text-cream-50 transition-colors cursor-pointer"
              >
                Yin Yoga Classes
              </button>
              <button
                onClick={() => scrollToSection('workshops')}
                className="block font-sans text-sm text-cream-100 hover:text-cream-50 transition-colors cursor-pointer"
              >
                Workshops
              </button>
              <button
                onClick={() => handlePageLink('/about')}
                className="block font-sans text-sm text-cream-100 hover:text-cream-50 transition-colors cursor-pointer"
              >
                Our Story
              </button>
              <button
                onClick={() => handlePageLink('/blog')}
                className="block font-sans text-sm text-cream-100 hover:text-cream-50 transition-colors cursor-pointer"
              >
                Blog
              </button>
              <button
                onClick={() => handlePageLink('/refund-policy')}
                className="block font-sans text-sm text-cream-100 hover:text-cream-50 transition-colors cursor-pointer"
              >
                Refund &amp; Return Policy
              </button>
              <a
                href="https://www.instagram.com/bodhira.co"
                target="_blank"
                rel="noopener noreferrer"
                className="block font-sans text-sm text-cream-100 hover:text-cream-50 transition-colors cursor-pointer"
              >
                @bodhira.co
              </a>
            </div>
          </div>

          <div>
            <h3 className="font-serif text-xl text-cream-50 mb-4">Stay Connected</h3>
            <p className="font-sans text-sm text-cream-100 mb-4">
              Join our community for wellness tips and exclusive offers.
            </p>
            <form data-readdy-form="true" onSubmit={handleSubmit} className="space-y-3">
              <input
                placeholder="Your email"
                className="w-full px-4 py-2 rounded-full bg-cream-50 text-sage-700 font-sans text-sm focus:outline-none focus:ring-2 focus:ring-sage-400"
                required
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-cream-50 text-sage-600 px-6 py-2 rounded-full font-sans text-sm hover:bg-cream-100 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
              >
                {isSubmitting ? 'Subscribing...' : 'Subscribe'}
              </button>
            </form>
            {submitMessage && (
              <p className={`text-xs mt-2 ${submitMessage.includes('successfully') ? 'text-green-200' : 'text-red-200'}`}>
                {submitMessage}
              </p>
            )}
          </div>
        </div>

        <div className="border-t border-cream-100/20 pt-8 text-center">
          <p className="font-sans text-sm text-cream-100">
            © 2026 Bodhira Co. All rights reserved. Made with love and intention.
          </p>
        </div>
      </div>
    </footer>
  );
}
